import { QueryClient } from '@tanstack/react-query';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Data is considered fresh for 60s. Previously staleTime was 0, so every
      // useQuery was stale immediately and refetchOnMount refired a network
      // request on every route change / dialog mount across ~39 page groups —
      // redundant load and visible spinner churn when the user had just
      // navigated. Write→read consistency is unaffected: the ~280
      // queryClient.invalidateQueries calls on mutations still force an
      // immediate refetch of the touched queries. refetchOnMount stays true, so
      // a mount still refetches once data is older than staleTime. A query that
      // genuinely needs real-time freshness can override staleTime: 0 locally.
      staleTime: 1000 * 60,
      gcTime: 1000 * 60 * 30,
      retry: 1,
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      refetchOnMount: true,
    },
    mutations: {
      // Never auto-retry mutations: POST/PUT/DELETE aren't idempotent, so a
      // retried create duplicates server-side effects (e.g. the orphaned
      // project copies from the 2026-06-12 bug). Server code owns its own
      // retries where they're safe (e.g. project-number collisions).
      retry: 0,
    },
  },
});

/**
 * Query options for an **aggregate dashboard** — a screen that reads across entities it
 * does not itself mutate (My Assignments, My Employees' Assignments).
 *
 * THE PROBLEM THIS SOLVES
 * ----------------------
 * Write→read consistency in this app rests on ~280 `invalidateQueries` calls, and every
 * one of them names the key of the entity it just wrote: `['tasks']`, `['tickets']`,
 * `['projects']`. React Query matches those by PREFIX — so they refresh every list keyed
 * `['tasks', …]`, and they match **nothing** keyed `['my-tasks', …]`,
 * `['employee-assignments']` or `['dept-pending-assignments', …]`.
 *
 * Combined with the 60 s `staleTime` above, that produced a real and confusing bug:
 * edit a task on the Tasks page, navigate back to an assignments dashboard within the
 * minute, and it renders the pre-edit data with no indication anything is out of date.
 * After 60 s it silently corrects itself, which is exactly the profile that makes a bug
 * hard to report — it depends on how fast you navigate.
 *
 * WHY THIS FIX AND NOT THE OTHERS
 * -------------------------------
 *  - Adding `invalidateQueries(['my-tasks'])` etc. at every mutation site means touching
 *    ~280 call sites and remembering to do it at the 281st. The dashboards would go stale
 *    again the first time someone forgot.
 *  - Renaming the keys to `['tasks', 'mine', …]` so the existing invalidations catch them
 *    by prefix is tidier in principle, but it makes every entity invalidation also refetch
 *    three `limit=all` dashboard queries, including for users who aren't on that page.
 *  - `staleTime: 0` is the escape hatch the comment above already anticipates. The cost is
 *    one refetch per visit to these screens; cached data still renders instantly while it
 *    revalidates in the background, so there's no spinner and no blank flash.
 *
 * `refetchOnWindowFocus` deliberately stays OFF. These dashboards fan out to several
 * `limit=all` requests, and alt-tabbing back to the browser is not evidence the data
 * changed — that would trade a stale-data bug for a request-storm one. Mount is the
 * signal that matters, because that's when the user actually navigated here.
 */
export const AGGREGATE_DASHBOARD_QUERY = {
  staleTime: 0,
} as const;

export default queryClient;
